import React, { useEffect, useState } from 'react';
import {
  styled,
  Typography,
  Table,
  TableCell,
  TableBody,
  TableRow,
  Box,
  TableContainer as MuiTableContainer,
  CircularProgress,
  Collapse,
  Button,
  LinearProgress,
  Tooltip,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import DoneIcon from '@material-ui/icons/Done';
import ErrorIcon from '@material-ui/icons/Error';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import dayjs from 'dayjs';

import { getActionStatuses } from 'reducers/essentialData';
import {
  resetActionStatuses,
  setLastSyncTimestamp,
  setShouldFetchEssentialData,
} from 'actions/essentialData';
import { RootDispatch } from 'reducers';
import { timestampInSeconds, snakeToCamel } from 'utils';
import { getClientCode } from 'reducers/Login';
import { getSetting } from 'reducers/configs/settings';
import darkCssColors from 'assets/themes/dark/colors.scss';

import {
  fetchEssentialData,
  invalidateLevelsUnrelatedToLoggedInLocationAndUser,
} from './actions';
import { ActionStatus as ActionStatusType } from './types';
import {
  getLastSyncTimestamp,
  invalidateAll,
  updateLastSyncTimestamp,
} from './utils';

function ActionStatus({ actionStatus }: { actionStatus: ActionStatusType }) {
  switch (actionStatus.status) {
    case 'ERROR':
      if (!actionStatus.error) return <ErrorIcon color="error" />;
      return (
        <Tooltip title={actionStatus.error} placement="top">
          <ErrorIcon color="error" />
        </Tooltip>
      );
    case 'DONE':
      return <DoneIcon />;
    case null:
    case 'LOADING':
      return <CircularProgress size={20} />;
    default:
      return null;
  }
}

const CenteredBox = styled('div')(({ theme }) => {
  const isDarkTheme =
    theme.palette.type === 'dark' && theme.palette.contrastThreshold < 7;
  return {
    width: '564px',
    maxWidth: '100%',
    height: 'auto',
    background: isDarkTheme
      ? darkCssColors.background
      : theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    pointerEvents: 'auto',
  };
});

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  padding: '1.25rem',
});

const TableContainer = styled(MuiTableContainer)({
  maxHeight: 'calc(100vh - 350px)',
  '& td, & th': {
    padding: '5px 16px',
    border: 'none',
  },
});

function EssentialDataLoader() {
  const { t } = useTranslation('essentialData');
  const dispatch: RootDispatch = useDispatch();

  const clientCode = useSelector(getClientCode);
  const timeUntilInvalidation = useSelector(
    getSetting('time_until_essential_data_invalidation'),
  );

  const [{ error, loading }, load] = useAsyncFn(async () => {
    dispatch(resetActionStatuses());
    dispatch(setShouldFetchEssentialData(true));

    const lastSyncTime = await getLastSyncTimestamp(clientCode);
    dispatch(setLastSyncTimestamp(lastSyncTime ?? 0));

    const timeOfInvalidation = dayjs((lastSyncTime ?? 0) * 1000).add(
      timeUntilInvalidation,
      'hour',
    );
    const shouldResync = !lastSyncTime || !dayjs().isBefore(timeOfInvalidation);

    if (shouldResync) {
      await dispatch(invalidateAll());
    } else {
      await dispatch(invalidateLevelsUnrelatedToLoggedInLocationAndUser());
    }
    await dispatch(fetchEssentialData());

    if (!lastSyncTime) {
      const syncTime = timestampInSeconds();
      await updateLastSyncTimestamp(clientCode, syncTime);
      dispatch(setLastSyncTimestamp(syncTime));
    }

    dispatch(setShouldFetchEssentialData(false));
  }, [dispatch, clientCode, timeUntilInvalidation]);

  useEffect(() => {
    load();
  }, [load]);

  const actionStatuses = useSelector(getActionStatuses);

  const totalActions = actionStatuses.length;
  const loadedActions = actionStatuses.filter(
    status => status.status === 'DONE',
  ).length;
  const failedActions = actionStatuses.filter(
    status => status.status === 'ERROR',
  ).length;

  const [openDetails, setOpenDetails] = useState(false);

  return (
    <CenteredBox data-testid="essentialDataLoader">
      <Content>
        <Typography variant="h5" data-testid="title">
          {t('initializationStep.title')}
        </Typography>
        <Typography variant="body2">
          <b>{t('initializationStep.status.label')}</b>{' '}
          {error || failedActions ? (
            <Typography
              variant="body2"
              component="span"
              color="error"
              data-testid="status"
            >
              {t('initializationStep.status.failed')}
            </Typography>
          ) : (
            <Typography variant="body2" component="span" data-testid="status">
              {loading && loadedActions < totalActions
                ? t('initializationStep.status.loading')
                : t('initializationStep.status.done')}
            </Typography>
          )}
          <Button
            variant="outlined"
            onClick={async () => {
              await dispatch(invalidateAll());
              load();
            }}
            style={{
              marginLeft: '1rem',
              visibility: error || failedActions ? 'visible' : 'hidden',
            }}
            data-testid="retryButton"
          >
            {t('initializationStep.retry')}
          </Button>
        </Typography>
        <LinearProgress
          variant="determinate"
          value={!totalActions ? 0 : (loadedActions / totalActions) * 100}
        />
      </Content>
      <Box display="flex" justifyContent="center">
        <Button
          onClick={() => setOpenDetails(prev => !prev)}
          data-testid="detailsButton"
        >
          {openDetails
            ? t('initializationStep.hideDetails')
            : t('initializationStep.showDetails')}
        </Button>
      </Box>
      <Collapse in={openDetails} unmountOnExit>
        <TableContainer>
          <Table>
            <TableBody>
              {actionStatuses.map(actionStatus => (
                <TableRow key={actionStatus.key}>
                  <TableCell>
                    {t(
                      `initializationStep.fetchedData.${snakeToCamel(
                        actionStatus.key.toLowerCase(),
                      )}`,
                    )}
                  </TableCell>
                  <TableCell>
                    <ActionStatus actionStatus={actionStatus} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </CenteredBox>
  );
}

export default EssentialDataLoader;
